import * as amplitude from "@amplitude/analytics-browser";
import { useCheckout } from "@stripe/react-stripe-js/checkout";
import { useRef, useState } from "react";

import { useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const PaymentError = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 15px;
  font-size: 14px;
  text-align: center;
  border: 1px solid #fcc;
`;

const PayNowButton = styled.button`
  width: 100%;
  background: #0b6dff;
  color: white;
  border: none;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 20px;

  &:hover:not(:disabled) {
    background:rgb(0, 105, 217);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ReminderText = styled.div`
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 140%;
    color: #000000;
    margin-top: 8px;

    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

type CreditCardFormProps = {
  /** web34_checkout_cta 'variation' arm: submit button reads "Try for $0.00"
   * instead of "Start trial". Only applies to trial checkouts (see below). */
  isWeb34Variation?: boolean;
};

export function CreditCardForm({ isWeb34Variation = false }: CreditCardFormProps) {
  const { t } = useTranslation();
  const checkout = useCheckout();
  // web29_trial_toggle: no-trial checkout (?trial=false) charges today, so the
  // submit button + reminder shouldn't talk about a trial.
  const isNoTrial =
    new URLSearchParams(window.location.search).get("trial") === "false";
  // web34_checkout_cta: pure copy change on the final submit button. The
  // no-trial (pay-today) label wins first, so paid checkouts are identical in
  // both arms — "Try for $0.00" is only shown where Due Today really is $0.00.
  // submitLabel stays English: it is the CheckoutSubmitClicked label sent to
  // Amplitude. The button shows the active language's copy (the no-trial
  // label is dormant web35 copy and stays English).
  const submitLabel = isNoTrial
    ? "Get my plan"
    : isWeb34Variation
      ? "Try for $0.00"
      : "Start trial";
  const submitText = isNoTrial
    ? submitLabel
    : isWeb34Variation
      ? t("checkout.card.submitTryFree")
      : t("checkout.card.submitStartTrial");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isCheckoutMounted = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [paymentElementLoaded, setPaymentElementLoaded] = useState(false);

  useEffect(() => {
    if (checkout.type === "success" && !isCheckoutMounted.current) {
      isCheckoutMounted.current = true;

      const paymentElement = checkout.checkout.createPaymentElement();
      paymentElement.mount("#payment-element");
      paymentElement.on("ready", () => {
        setPaymentElementLoaded(true);
      });
    }
  }, [checkout]);

  useEffect(() => {
    if (paymentElementLoaded) {
      containerRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [paymentElementLoaded]);

  const handlePaymentSubmit = async (event: any) => {
    event.preventDefault();
    if (checkout.type !== "success" || !checkout) {
      console.warn("Checkout is not ready");
      return;
    }

    setErrorMessage(null);
    setIsLoading(true);
    // web34_checkout_cta: the submit click itself was previously untracked
    // (only the server-side trial event was visible); label = rendered copy.
    amplitude.track("CheckoutSubmitClicked", { label: submitLabel });

    const result = await checkout.checkout.confirm();
    setIsLoading(false);

    // confirm sends payment details etc. using the PaymentElement
    if (result.type === "error") {
      setErrorMessage(result.error.message);
    } else {
      // success — Stripe handles redirection where required
      // result usually contains no error, probably redirect being handled internally
      console.log("Payment successful", result);
    }
  };

  return (
    <div ref={containerRef}>
      <form onSubmit={handlePaymentSubmit}>
        <div id="payment-element" style={{ marginTop: "16px" }} />
        {errorMessage && <PaymentError>{errorMessage}</PaymentError>}
        <ReminderText>
          {isNoTrial
            ? "Covered by our 30-day money-back guarantee."
            : t("checkout.card.trialReminder")}
        </ReminderText>
        <div>
          <PayNowButton type="submit" disabled={isLoading}>
            {submitText}
          </PayNowButton>
        </div>
      </form>
    </div>
  );     
}
